import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  MdDownload,
  MdLocationOn,
  MdSearch,
  MdTimer,
  MdUpload
} from 'react-icons/md'
import { useParams } from 'react-router-dom'

import useFollowUpExterno from '@hook/followUpFornecedor/use-externo'
import ExternoProvider from '@provider/FollowUpFornecedor/ExternoProvider'

import {
  parseCSVLine,
  normalizeHeader
} from '../../../utils/followUpFornecedor/csv'
import ExternoFooter from './ExternoFooter'
import ExternoHeader from './ExternoHeader'
import SplashScreen, { useSplash } from './SplashScreen'
import TelaExpirado from './TelaExpirado'
import TelaVerificacao from './TelaVerificacao'

const PAGE_SIZE = 50

function parseDateLocal(dateStr: string): Date {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}

type UploadRow = {
  documentId: number
  productId: number
  nomeProduto: string
  novaQuantidade: number | null
  novaDataEntrega: string | null
  observacao: string | null
  prevQuantidade: number | null
  prevDataEntrega: string | null
  prevObservacao: string | null
}

const ExternoContent: FC<{ token: string }> = ({ token }) => {
  const [page, setPage] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [batchData, setBatchData] = useState('')
  const [batchObs, setBatchObs] = useState('')
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set())
  const [codigo, setCodigo] = useState('')
  const [sortKey, setSortKey] = useState<string>('dataEstimada')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [uploadModal, setUploadModal] = useState<{
    open: boolean
    rows: UploadRow[]
  }>({ open: false, rows: [] })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const toast = useToast()

  const {
    state,
    data,
    edits,
    loadingSubmit,
    loadingVerificacao,
    codigoError,
    loadByToken,
    handleEditField,
    handleSubmit
  } = useFollowUpExterno()

  const rawItems = data?.items ?? []
  const items = useMemo(() => {
    const arr = [...rawItems]
    arr.sort((a, b) => {
      let va: any
      let vb: any
      if (sortKey === 'dataEstimada') {
        va = new Date(a.dataEstimada).getTime()
        vb = new Date(b.dataEstimada).getTime()
      } else if (sortKey === 'documentId') {
        va = a.documentId
        vb = b.documentId
      } else if (sortKey === 'fornecedor') {
        va = a.fornecedor?.toLowerCase() ?? ''
        vb = b.fornecedor?.toLowerCase() ?? ''
      } else if (sortKey === 'nomeProduto') {
        va = a.nomeProduto?.toLowerCase() ?? ''
        vb = b.nomeProduto?.toLowerCase() ?? ''
      } else if (sortKey === 'aFornecer') {
        va = a.aFornecer
        vb = b.aFornecer
      } else if (sortKey === 'aFornecerReais') {
        va = a.aFornecerReais
        vb = b.aFornecerReais
      } else if (sortKey === 'dataCriacao') {
        va = a.dataCriacao ? new Date(a.dataCriacao).getTime() : 0
        vb = b.dataCriacao ? new Date(b.dataCriacao).getTime() : 0
      } else if (sortKey === 'productId') {
        va = a.productId
        vb = b.productId
      } else {
        va = 0
        vb = 0
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return arr
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.items, sortKey, sortDir])

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items
    const q = searchTerm.toLowerCase()
    return items.filter(
      item =>
        String(item.documentId).includes(q) ||
        String(item.productId).includes(q) ||
        item.fornecedor?.toLowerCase().includes(q) ||
        item.nomeProduto?.toLowerCase().includes(q) ||
        (item.codigoFornecedorProduto ?? '').toLowerCase().includes(q) ||
        (item.dataCriacao ?? '').includes(q) ||
        String(item.aFornecer).includes(q)
    )
  }, [items, searchTerm])

  useEffect(() => {
    const saved = sessionStorage.getItem(`fu-codigo-${token}`)
    if (saved) {
      setCodigo(saved)
      loadByToken(token, saved)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (state === 'editavel') {
      sessionStorage.setItem(`fu-codigo-${token}`, codigo)
    }
  }, [state, codigo, token])

  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      const hasChanges =
        state === 'editavel' &&
        edits.some(
          e =>
            e.novaQuantidade !== null ||
            e.novaDataEntrega !== null ||
            e.observacao !== null
        )
      if (hasChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [edits, state])

  if (state === 'verificacao') {
    return (
      <TelaVerificacao
        codigo={codigo}
        setCodigo={setCodigo}
        loading={loadingVerificacao}
        error={codigoError}
        onSubmit={() => loadByToken(token, codigo)}
      />
    )
  }

  if (state === 'expirado') return <TelaExpirado />

  const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE)
  const paginatedItems = filteredItems.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE
  )
  const firstItem = items[0]
  const isSubmitted = data?.isSubmitted ?? false

  const rowKey = (documentId: number, productId: number) =>
    `${documentId}-${productId}`

  const toggleRow = (key: string) => {
    setSelectedRows(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleAll = () => {
    if (selectedRows.size === filteredItems.length) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(
        new Set(filteredItems.map(i => rowKey(i.documentId, i.productId)))
      )
    }
  }

  const targetItems =
    selectedRows.size > 0
      ? filteredItems.filter(i =>
          selectedRows.has(rowKey(i.documentId, i.productId))
        )
      : filteredItems

  const handleExport = () => {
    const header =
      'Doc.;Prod.ID;Fornecedor;Produto;Cód. Fornecedor;Data Criação;Data Estimada;A Fornecer;Valor Total;Rupt. 0d;Rupt. 10d;Nova Qtd.;Nova Data;Observação'
    const fmtRuptura = (v: boolean | null) =>
      v === true ? 'Sim' : v === false ? 'Não' : ''
    const rows = filteredItems.map(item => {
      const edit = edits.find(
        e => e.documentId === item.documentId && e.productId === item.productId
      )
      return [
        item.documentId,
        item.productId,
        `"${item.fornecedor}"`,
        `"${item.nomeProduto}"`,
        item.codigoFornecedorProduto ?? '',
        item.dataCriacao
          ? parseDateLocal(item.dataCriacao).toLocaleDateString('pt-BR')
          : '',
        parseDateLocal(item.dataEstimada).toLocaleDateString('pt-BR'),
        item.aFornecer,
        item.aFornecerReais,
        fmtRuptura(item.rupturaZeroDias),
        fmtRuptura(item.rupturaDezDias),
        edit?.novaQuantidade ?? '',
        edit?.novaDataEntrega
          ? parseDateLocal(edit.novaDataEntrega).toLocaleDateString('pt-BR')
          : '',
        `"${edit?.observacao ?? ''}"`
      ].join(';')
    })
    fileDownload(
      '\uFEFF' + [header, ...rows].join('\r\n'),
      'follow-up-pedidos.csv',
      'text/csv;charset=utf-8'
    )
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      let text = (ev.target?.result as string) ?? ''
      if (text.charCodeAt(0) === 0xfeff) text = text.slice(1)

      const lines = text.split(/\r?\n/).filter(l => l.trim())
      if (lines.length < 2) {
        toast({
          title: 'Arquivo inválido ou vazio',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        })
        return
      }

      const sep = lines[0].includes(';') ? ';' : ','
      const headers = parseCSVLine(lines[0], sep).map(normalizeHeader)
      const idx = (term: string) =>
        headers.findIndex(h => h.includes(normalizeHeader(term)))

      const idxDoc = idx('doc.')
      const idxProd = idx('prod')
      const idxQtd = idx('nova qtd')
      const idxData = idx('nova data')
      const idxObs = idx('observa')

      if (idxDoc === -1 || idxProd === -1) {
        toast({
          title: 'Formato inválido: colunas "Doc." e "Prod.ID" não encontradas',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top'
        })
        return
      }

      const changed: UploadRow[] = []
      for (const line of lines.slice(1)) {
        const cols = parseCSVLine(line, sep)
        const documentId = Number(cols[idxDoc])
        const productId = Number(cols[idxProd])
        if (!documentId || !productId) continue

        const qtdStr = idxQtd >= 0 ? cols[idxQtd]?.trim() : ''
        const dateStr = idxData >= 0 ? cols[idxData]?.trim() : ''
        const obsStr = idxObs >= 0 ? cols[idxObs]?.trim() ?? '' : ''

        const novaQuantidade = qtdStr ? Number(qtdStr.replace(',', '.')) : null
        let novaDataEntrega: string | null = null
        if (dateStr) {
          if (dateStr.includes('/')) {
            const parts = dateStr.split('/')
            if (parts.length === 3) {
              const [d, m, y] = parts
              novaDataEntrega = `${y}-${m.padStart(2, '0')}-${d.padStart(
                2,
                '0'
              )}`
            }
          } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
            novaDataEntrega = dateStr.slice(0, 10)
          }
        }
        const observacao = obsStr || null

        const current = edits.find(
          e => e.documentId === documentId && e.productId === productId
        )
        const itemInfo = items.find(
          i => i.documentId === documentId && i.productId === productId
        )
        if (!itemInfo) continue

        const prevQtd = current?.novaQuantidade ?? null
        const prevDate = current?.novaDataEntrega?.slice(0, 10) ?? null
        const prevObs = current?.observacao ?? null

        const hasChange =
          novaQuantidade !== prevQtd ||
          novaDataEntrega !== prevDate ||
          observacao !== prevObs

        if (hasChange) {
          changed.push({
            documentId,
            productId,
            nomeProduto: itemInfo.nomeProduto,
            novaQuantidade,
            novaDataEntrega,
            observacao,
            prevQuantidade: prevQtd,
            prevDataEntrega: current?.novaDataEntrega ?? null,
            prevObservacao: prevObs
          })
        }
      }

      if (changed.length === 0) {
        toast({
          title: 'Nenhuma alteração encontrada no arquivo',
          description: `Separador detectado: "${sep}" · ${
            lines.length - 1
          } linha(s) lida(s)`,
          status: 'info',
          duration: 4000,
          isClosable: true,
          position: 'top'
        })
      } else {
        setUploadModal({ open: true, rows: changed })
      }
    }
    reader.readAsText(file, 'UTF-8')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const confirmUpload = () => {
    uploadModal.rows.forEach(row => {
      handleEditField(
        row.documentId,
        row.productId,
        'novaQuantidade',
        row.novaQuantidade
      )
      handleEditField(
        row.documentId,
        row.productId,
        'novaDataEntrega',
        row.novaDataEntrega
      )
      handleEditField(
        row.documentId,
        row.productId,
        'observacao',
        row.observacao
      )
    })
    setUploadModal({ open: false, rows: [] })
    toast({
      title: `${uploadModal.rows.length} item(s) atualizado(s) com sucesso`,
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top'
    })
  }

  return (
    <Box bg="#f8fafc" minH="calc(100vh - 64px)">
      <Box maxW="1600px" mx="auto" px={{ base: 2, md: 6 }} py={8}>
        {/* Hero Card */}
        <Box
          bg="linear-gradient(135deg, #0f5238 0%, #1d5840 60%, #134f35 100%)"
          color="white"
          p={{ base: 6, md: 8 }}
          borderRadius="2xl"
          mb={6}
        >
          <Text
            fontSize={{ base: '22px', md: '28px' }}
            fontWeight="800"
            mb={1}
            letterSpacing="-0.5px"
          >
            Confirmação de Pedidos
          </Text>
          {firstItem?.centro && (
            <HStack spacing={1} mb={3}>
              <Icon as={MdLocationOn} boxSize={4} color="whiteAlpha.700" />
              <Text fontSize="14px" color="whiteAlpha.800" fontWeight="500">
                {firstItem.centro}
              </Text>
            </HStack>
          )}
          <Text
            fontSize="15px"
            color="whiteAlpha.800"
            mb={4}
            maxW="520px"
            lineHeight="1.6"
          >
            Revise os itens do seu pedido. Informe as alterações necessárias em
            quantidade ou datas para prosseguirmos.
          </Text>
          {data?.expiresAt && (
            <Box
              display="inline-flex"
              alignItems="center"
              gap={2}
              bg="whiteAlpha.200"
              border="1px solid"
              borderColor="whiteAlpha.300"
              borderRadius="full"
              px={4}
              py="6px"
              fontSize="13px"
              fontWeight="500"
            >
              <Icon as={MdTimer} boxSize={4} />
              <Text as="span">
                Link válido até{' '}
                <Text as="span" fontWeight="700">
                  {new Date(data.expiresAt).toLocaleString('pt-BR')}
                </Text>
              </Text>
            </Box>
          )}
        </Box>

        {/* Banner reenviar */}
        {isSubmitted && (
          <Alert
            status="warning"
            borderRadius="xl"
            mb={5}
            bg="orange.50"
            border="1px solid"
            borderColor="orange.200"
            fontSize="14px"
          >
            <AlertIcon color="orange.400" />
            <Text>
              Você já enviou uma resposta para este pedido. Pode editar e{' '}
              <Text as="span" fontWeight="700">
                reenviar
              </Text>{' '}
              se necessário.
            </Text>
          </Alert>
        )}

        {!isSubmitted && (
          <Alert
            status="info"
            borderRadius="xl"
            mb={5}
            bg="blue.50"
            border="1px solid"
            borderColor="blue.200"
            fontSize="14px"
          >
            <AlertIcon />
            Preencha apenas os campos com alterações. Enviar sem preencher
            significa ciente, sem mudanças.
          </Alert>
        )}

        {/* Barra de ações */}
        <Flex
          justify="space-between"
          align="center"
          mb={3}
          gap={2}
          flexWrap="wrap"
        >
          <InputGroup size="sm" maxW="340px">
            <InputLeftElement pointerEvents="none">
              <Icon as={MdSearch} color="gray.400" boxSize={4} />
            </InputLeftElement>
            <Input
              pl={8}
              placeholder="Buscar por pedido, produto, fornecedor..."
              borderColor="gray.300"
              borderRadius="md"
              bg="white"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              _focus={{
                borderColor: '#0f5238',
                boxShadow: '0 0 0 1px #0f5238'
              }}
            />
          </InputGroup>
          <HStack spacing={2}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              size="sm"
              variant="outline"
              borderColor="gray.300"
              color="gray.600"
              leftIcon={<Icon as={MdUpload} boxSize={4} />}
              borderRadius="md"
              onClick={() => fileInputRef.current?.click()}
              _hover={{ bg: 'gray.50' }}
            >
              Importar CSV
            </Button>
            <Button
              size="sm"
              variant="outline"
              borderColor="gray.300"
              color="gray.600"
              leftIcon={<Icon as={MdDownload} boxSize={4} />}
              borderRadius="md"
              onClick={handleExport}
              _hover={{ bg: 'gray.50' }}
            >
              Exportar CSV
            </Button>
          </HStack>
        </Flex>

        {/* Aplicar em lote */}
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="xl"
          px={5}
          py={4}
          mb={4}
          boxShadow="sm"
        >
          <Flex align="center" justify="space-between" mb={3}>
            <Text
              fontSize="11px"
              fontWeight="700"
              color="gray.500"
              letterSpacing="1px"
              textTransform="uppercase"
            >
              ⚡ Aplicar em lote
              {selectedRows.size > 0 && (
                <Text as="span" color="#0f5238" ml={2}>
                  ({selectedRows.size} selecionado
                  {selectedRows.size > 1 ? 's' : ''})
                </Text>
              )}
            </Text>
          </Flex>
          <HStack spacing={4} flexWrap="wrap">
            <HStack spacing={2}>
              <Input
                size="sm"
                type="date"
                w="160px"
                borderColor="gray.300"
                borderRadius="md"
                value={batchData}
                onChange={e => setBatchData(e.target.value)}
                _focus={{
                  borderColor: '#0f5238',
                  boxShadow: '0 0 0 1px #0f5238'
                }}
              />
              <Button
                size="sm"
                bg="#0f5238"
                color="white"
                borderRadius="md"
                _hover={{ bg: '#1d5840' }}
                isDisabled={!batchData || !targetItems.length}
                onClick={() => {
                  targetItems.forEach(item =>
                    handleEditField(
                      item.documentId,
                      item.productId,
                      'novaDataEntrega',
                      batchData
                    )
                  )
                  setBatchData('')
                }}
              >
                Aplicar Data
              </Button>
            </HStack>

            <Box
              w="1px"
              h="28px"
              bg="gray.200"
              display={{ base: 'none', md: 'block' }}
            />

            <HStack spacing={2}>
              <Input
                size="sm"
                w="200px"
                borderColor="gray.300"
                borderRadius="md"
                placeholder="Observação para todos"
                value={batchObs}
                onChange={e => setBatchObs(e.target.value)}
                _focus={{
                  borderColor: '#0f5238',
                  boxShadow: '0 0 0 1px #0f5238'
                }}
              />
              <Button
                size="sm"
                bg="#0f5238"
                color="white"
                borderRadius="md"
                _hover={{ bg: '#1d5840' }}
                isDisabled={!batchObs || !targetItems.length}
                onClick={() => {
                  targetItems.forEach(item =>
                    handleEditField(
                      item.documentId,
                      item.productId,
                      'observacao',
                      batchObs
                    )
                  )
                  setBatchObs('')
                }}
              >
                Aplicar Obs.
              </Button>
            </HStack>

            <Box
              w="1px"
              h="28px"
              bg="gray.200"
              display={{ base: 'none', md: 'block' }}
            />

            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              borderRadius="md"
              isDisabled={!targetItems.length}
              onClick={() => {
                targetItems.forEach(item => {
                  handleEditField(
                    item.documentId,
                    item.productId,
                    'novaQuantidade',
                    null
                  )
                  handleEditField(
                    item.documentId,
                    item.productId,
                    'novaDataEntrega',
                    null
                  )
                  handleEditField(
                    item.documentId,
                    item.productId,
                    'observacao',
                    null
                  )
                })
              }}
            >
              Limpar tudo
            </Button>
          </HStack>
        </Box>

        {/* Tabela */}
        <Box
          bg="white"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
          overflow="hidden"
          mb={4}
        >
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr
                  bg="gray.50"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <Th py={3} w="40px">
                    <Checkbox
                      isChecked={
                        selectedRows.size === filteredItems.length &&
                        filteredItems.length > 0
                      }
                      isIndeterminate={
                        selectedRows.size > 0 &&
                        selectedRows.size < filteredItems.length
                      }
                      onChange={toggleAll}
                      colorScheme="green"
                      sx={{
                        '& .chakra-checkbox__control': {
                          borderColor: 'gray.400',
                          borderWidth: '2px'
                        }
                      }}
                    />
                  </Th>
                  {(
                    [
                      { label: 'Doc.', key: 'documentId', minW: '80px' },
                      { label: 'Fornecedor', key: 'fornecedor', minW: '140px' },
                      { label: 'Produto', key: 'nomeProduto', minW: '200px' },
                      {
                        label: 'Cód. Produto',
                        key: 'productId',
                        minW: '110px'
                      },
                      { label: 'Cód. Fornecedor', key: null, minW: '140px' },
                      {
                        label: 'Data Criação',
                        key: 'dataCriacao',
                        minW: '120px'
                      },
                      {
                        label: 'Data Estimada',
                        key: 'dataEstimada',
                        minW: '120px'
                      },
                      { label: 'A Fornecer', key: 'aFornecer', minW: '110px' },
                      {
                        label: 'Valor Total',
                        key: 'aFornecerReais',
                        minW: '120px'
                      },
                      { label: 'Rupt. 0d', key: null, minW: '80px' },
                      { label: 'Rupt. 10d', key: null, minW: '85px' },
                      { label: 'Nova Qtd.', key: null, minW: '110px' },
                      { label: 'Nova Data', key: null, minW: '145px' },
                      { label: 'Observação', key: null, minW: '200px' }
                    ] as { label: string; key: string | null; minW: string }[]
                  ).map(col => {
                    const isActive = col.key && sortKey === col.key
                    const arrow = isActive
                      ? sortDir === 'asc'
                        ? ' ▲'
                        : ' ▼'
                      : ''
                    return (
                      <Th
                        key={col.label}
                        fontSize="11px"
                        fontWeight="700"
                        letterSpacing="0.8px"
                        color={isActive ? '#0f5238' : 'gray.500'}
                        textTransform="uppercase"
                        py={3}
                        minW={col.minW}
                        cursor={col.key ? 'pointer' : 'default'}
                        userSelect="none"
                        _hover={col.key ? { color: '#0f5238' } : undefined}
                        onClick={() => {
                          if (!col.key) return
                          if (sortKey === col.key) {
                            setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
                          } else {
                            setSortKey(col.key)
                            setSortDir('asc')
                            setPage(0)
                          }
                        }}
                      >
                        {col.label}
                        {arrow}
                      </Th>
                    )
                  })}
                </Tr>
              </Thead>
              <Tbody>
                {paginatedItems.map(item => {
                  const key = rowKey(item.documentId, item.productId)
                  const edit = edits.find(
                    e =>
                      e.documentId === item.documentId &&
                      e.productId === item.productId
                  )
                  const isSelected = selectedRows.has(key)
                  return (
                    <Tr
                      key={key}
                      bg={isSelected ? 'green.50' : undefined}
                      _hover={{ bg: isSelected ? 'green.50' : 'gray.50' }}
                      transition="background 0.1s"
                      borderBottom="1px solid"
                      borderColor="gray.100"
                    >
                      <Td>
                        <Checkbox
                          isChecked={isSelected}
                          onChange={() => toggleRow(key)}
                          colorScheme="green"
                          sx={{
                            '& .chakra-checkbox__control': {
                              borderColor: 'gray.400',
                              borderWidth: '2px'
                            }
                          }}
                        />
                      </Td>
                      <Td fontSize="12px" color="gray.500">
                        {item.documentId}
                      </Td>
                      <Td fontSize="13px" color="gray.600" fontWeight="500">
                        {item.fornecedor}
                      </Td>
                      <Td py={4}>
                        <Text fontWeight="600" fontSize="13px" color="gray.800">
                          {item.nomeProduto}
                        </Text>
                      </Td>
                      <Td fontSize="13px" color="gray.600">
                        {item.productId}
                      </Td>
                      <Td fontSize="13px" color="gray.600">
                        {item.codigoFornecedorProduto ?? '—'}
                      </Td>
                      <Td fontSize="13px" color="gray.600">
                        {item.dataCriacao
                          ? parseDateLocal(item.dataCriacao).toLocaleDateString(
                              'pt-BR'
                            )
                          : '—'}
                      </Td>
                      <Td fontSize="13px" color="gray.600">
                        {parseDateLocal(item.dataEstimada).toLocaleDateString(
                          'pt-BR'
                        )}
                      </Td>
                      <Td>
                        <Text fontSize="13px" fontWeight="700" color="gray.800">
                          {item.aFornecer.toLocaleString('pt-BR')} un
                        </Text>
                      </Td>
                      <Td fontSize="13px" color="gray.600">
                        {item.aFornecerReais.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        })}
                      </Td>
                      <Td>
                        {item.rupturaZeroDias === true ? (
                          <Tooltip
                            label="Produto em ruptura hoje"
                            hasArrow
                            placement="top"
                          >
                            <Box
                              display="inline-block"
                              bg="red.100"
                              color="red.700"
                              borderRadius="full"
                              px={2}
                              py="1px"
                              fontSize="11px"
                              fontWeight="700"
                              cursor="default"
                            >
                              Sim
                            </Box>
                          </Tooltip>
                        ) : item.rupturaZeroDias === false ? (
                          <Box
                            display="inline-block"
                            bg="green.50"
                            color="green.600"
                            borderRadius="full"
                            px={2}
                            py="1px"
                            fontSize="11px"
                            fontWeight="600"
                          >
                            Não
                          </Box>
                        ) : (
                          <Text fontSize="12px" color="gray.300">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td>
                        {item.rupturaDezDias === true ? (
                          <Tooltip
                            label="Produto em ruptura em 10 dias"
                            hasArrow
                            placement="top"
                          >
                            <Box
                              display="inline-block"
                              bg="orange.100"
                              color="orange.700"
                              borderRadius="full"
                              px={2}
                              py="1px"
                              fontSize="11px"
                              fontWeight="700"
                              cursor="default"
                            >
                              Sim
                            </Box>
                          </Tooltip>
                        ) : item.rupturaDezDias === false ? (
                          <Box
                            display="inline-block"
                            bg="green.50"
                            color="green.600"
                            borderRadius="full"
                            px={2}
                            py="1px"
                            fontSize="11px"
                            fontWeight="600"
                          >
                            Não
                          </Box>
                        ) : (
                          <Text fontSize="12px" color="gray.300">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <Input
                          size="sm"
                          type="number"
                          w="88px"
                          borderColor="gray.300"
                          borderRadius="md"
                          textAlign="center"
                          fontWeight="600"
                          value={edit?.novaQuantidade ?? ''}
                          onChange={e =>
                            handleEditField(
                              item.documentId,
                              item.productId,
                              'novaQuantidade',
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          _focus={{
                            borderColor: '#0f5238',
                            boxShadow: '0 0 0 1px #0f5238'
                          }}
                        />
                      </Td>
                      <Td>
                        <Input
                          size="sm"
                          type="date"
                          w="135px"
                          borderColor="gray.300"
                          borderRadius="md"
                          value={edit?.novaDataEntrega?.slice(0, 10) ?? ''}
                          onChange={e =>
                            handleEditField(
                              item.documentId,
                              item.productId,
                              'novaDataEntrega',
                              e.target.value || null
                            )
                          }
                          _focus={{
                            borderColor: '#0f5238',
                            boxShadow: '0 0 0 1px #0f5238'
                          }}
                        />
                      </Td>
                      <Td>
                        <Textarea
                          size="sm"
                          w="190px"
                          minH="36px"
                          rows={1}
                          borderColor="gray.300"
                          borderRadius="md"
                          placeholder="Observação..."
                          value={edit?.observacao ?? ''}
                          resize="none"
                          onChange={e =>
                            handleEditField(
                              item.documentId,
                              item.productId,
                              'observacao',
                              e.target.value || null
                            )
                          }
                          _focus={{
                            borderColor: '#0f5238',
                            boxShadow: '0 0 0 1px #0f5238'
                          }}
                        />
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </Box>

          {/* Rodapé da tabela com paginação */}
          <Flex
            align="center"
            justify="space-between"
            px={5}
            py={3}
            borderTop="1px solid"
            borderColor="gray.100"
            bg="gray.50"
          >
            <Text fontSize="13px" color="gray.500">
              Mostrando{' '}
              <Text as="span" fontWeight="600" color="gray.700">
                {Math.min((page + 1) * PAGE_SIZE, filteredItems.length)}
              </Text>{' '}
              de{' '}
              <Text as="span" fontWeight="600" color="gray.700">
                {filteredItems.length}
              </Text>
              {searchTerm && filteredItems.length !== items.length && (
                <Text as="span" color="gray.400">
                  {' '}
                  (de {items.length} no total)
                </Text>
              )}{' '}
              registros
            </Text>

            {totalPages > 1 && (
              <HStack spacing={1}>
                <IconButton
                  aria-label="Anterior"
                  icon={<Text fontSize="14px">‹</Text>}
                  size="sm"
                  variant="ghost"
                  color="gray.500"
                  isDisabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                />
                {Array.from({ length: totalPages }, (_, i) => (
                  <Button
                    key={i}
                    size="sm"
                    borderRadius="full"
                    minW="36px"
                    fontWeight={page === i ? '700' : '400'}
                    bg={page === i ? '#0f5238' : 'transparent'}
                    color={page === i ? 'white' : 'gray.600'}
                    _hover={page === i ? { bg: '#1d5840' } : { bg: 'gray.100' }}
                    onClick={() => setPage(i)}
                  >
                    {i + 1}
                  </Button>
                ))}
                <IconButton
                  aria-label="Próximo"
                  icon={<Text fontSize="14px">›</Text>}
                  size="sm"
                  variant="ghost"
                  color="gray.500"
                  isDisabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                />
              </HStack>
            )}
          </Flex>
        </Box>

        {/* FAB */}
        <Box position="fixed" bottom={8} right={8} zIndex={100}>
          <Button
            size="lg"
            bg="#0f5238"
            color="white"
            fontWeight="700"
            px={8}
            py={7}
            borderRadius="xl"
            isLoading={loadingSubmit}
            loadingText="Enviando..."
            onClick={handleSubmit}
            boxShadow="0 8px 32px rgba(15,82,56,0.45), 0 2px 8px rgba(0,0,0,0.15)"
            _hover={{
              bg: '#1d5840',
              transform: 'translateY(-2px)',
              boxShadow:
                '0 14px 40px rgba(15,82,56,0.55), 0 4px 12px rgba(0,0,0,0.18)'
            }}
            _active={{ bg: '#0a3d29', transform: 'translateY(0)' }}
            transition="all 0.2s ease"
            sx={{
              '@keyframes fabPulse': {
                '0%, 100%': {
                  boxShadow:
                    '0 8px 32px rgba(15,82,56,0.45), 0 2px 8px rgba(0,0,0,0.15)'
                },
                '50%': {
                  boxShadow:
                    '0 8px 40px rgba(15,82,56,0.7), 0 2px 8px rgba(0,0,0,0.18)'
                }
              },
              animation: 'fabPulse 2.5s ease-in-out infinite'
            }}
          >
            {isSubmitted ? '↺ Reenviar' : '✔ Confirmar e Enviar'}
          </Button>
        </Box>
      </Box>

      {/* Modal Upload */}
      <Modal
        isOpen={uploadModal.open}
        onClose={() => setUploadModal({ open: false, rows: [] })}
        isCentered
        scrollBehavior="inside"
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            px={4}
            py={3}
            bg="#0f5238"
            color="white"
            borderTopRadius={5}
            borderBottom="3px solid #ECC94B"
            fontSize="15px"
            fontWeight="700"
          >
            <Flex align="center" gap={2}>
              <Icon as={MdUpload} boxSize={5} />
              Confirmar Importação —
              <Badge
                bg="whiteAlpha.300"
                color="white"
                borderRadius="full"
                px={2}
              >
                {uploadModal.rows.length} item
                {uploadModal.rows.length !== 1 ? 's' : ''} com alteração
              </Badge>
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" top={3} />
          <ModalBody bg="white" py={4} px={4}>
            <Text fontSize="13px" color="gray.600" mb={4}>
              Os campos abaixo serão aplicados na grade. Revise antes de
              confirmar.
            </Text>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Produto</Th>
                    <Th isNumeric>Nova Qtd.</Th>
                    <Th>Nova Data</Th>
                    <Th>Observação</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {uploadModal.rows.map(row => {
                    const fmtDate = (d: string | null) => {
                      if (!d) return '—'
                      const dt = parseDateLocal(d)
                      return isNaN(dt.getTime())
                        ? d
                        : dt.toLocaleDateString('pt-BR')
                    }
                    return (
                      <Tr key={`${row.documentId}-${row.productId}`}>
                        <Td>
                          <Text fontWeight="600" fontSize="13px">
                            {row.nomeProduto}
                          </Text>
                          <Text fontSize="11px" color="gray.400">
                            Doc. {row.documentId}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          {row.novaQuantidade !== row.prevQuantidade ? (
                            <Flex justify="flex-end" align="center" gap={1}>
                              {row.prevQuantidade !== null && (
                                <Text as="s" fontSize="11px" color="gray.400">
                                  {row.prevQuantidade}
                                </Text>
                              )}
                              <Text
                                fontWeight="700"
                                color="green.600"
                                fontSize="13px"
                              >
                                {row.novaQuantidade ?? '—'}
                              </Text>
                            </Flex>
                          ) : (
                            <Text fontSize="13px" color="gray.400">
                              sem alteração
                            </Text>
                          )}
                        </Td>
                        <Td>
                          {row.novaDataEntrega !==
                          (row.prevDataEntrega?.slice(0, 10) ?? null) ? (
                            <Flex direction="column">
                              {row.prevDataEntrega && (
                                <Text as="s" fontSize="11px" color="gray.400">
                                  {fmtDate(row.prevDataEntrega)}
                                </Text>
                              )}
                              <Text
                                fontWeight="700"
                                color="green.600"
                                fontSize="13px"
                              >
                                {fmtDate(row.novaDataEntrega)}
                              </Text>
                            </Flex>
                          ) : (
                            <Text fontSize="13px" color="gray.400">
                              sem alteração
                            </Text>
                          )}
                        </Td>
                        <Td maxW="220px">
                          {row.observacao !== row.prevObservacao ? (
                            <Flex direction="column">
                              {row.prevObservacao && (
                                <Text
                                  as="s"
                                  fontSize="11px"
                                  color="gray.400"
                                  noOfLines={1}
                                >
                                  {row.prevObservacao}
                                </Text>
                              )}
                              <Text
                                fontSize="13px"
                                color={row.observacao ? 'gray.700' : 'gray.400'}
                                fontStyle={row.observacao ? 'italic' : 'normal'}
                                noOfLines={2}
                              >
                                {row.observacao ?? '—'}
                              </Text>
                            </Flex>
                          ) : (
                            <Text fontSize="13px" color="gray.400">
                              sem alteração
                            </Text>
                          )}
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </Box>
          </ModalBody>
          <ModalFooter bg="white" borderBottomRadius={5} gap={2}>
            <Button
              variant="outline"
              borderColor="gray.300"
              color="gray.600"
              onClick={() => setUploadModal({ open: false, rows: [] })}
            >
              Cancelar
            </Button>
            <Button
              bg="#0f5238"
              color="white"
              _hover={{ bg: '#1d5840' }}
              leftIcon={<Icon as={MdUpload} />}
              onClick={confirmUpload}
            >
              Confirmar e Aplicar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

const FollowUpFornecedorExterno: FC = () => {
  const { token } = useParams<{ token: string }>()
  const splashPhase = useSplash()

  return (
    <Box bg="#f8fafc" minH="100vh">
      <SplashScreen phase={splashPhase} />
      <ExternoHeader splashPhase={splashPhase} />
      <ExternoProvider>
        <ExternoContent token={token} />
      </ExternoProvider>
      <ExternoFooter />
    </Box>
  )
}

export default FollowUpFornecedorExterno
